import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable, { type CardSpec, type Column } from '../DataTable';
import StatusBadge from '../StatusBadge';
import { useTaskRowActions } from './useTaskRowActions';
import { DueWithLate, taskListColumns, type TaskListRow } from './taskColumns';
import { assigneeNames, taskPath } from './taskRowModel';
import { useIsDesktop } from '../ui/useOverlay';
import { useTaskDeleteAction } from './useTaskDeleteAction';
import { useWithoutHidden } from '../../utils/undoableActions';
import { DELETE_KINDS } from '../../utils/undoableDeletes';

/**
 * P-06/E2 (D-62, D-44) — a task table with the SAME row as "כל המשימות":
 * ▶ first, the D-62 columns (`hide` the ones the screen already states), and
 * ⋯ (📎, logs, edit, cancel). Used by the "משימות" tabs of an engagement and
 * of a customer; a click opens the task page.
 */
export default function TaskRowsTable<T extends TaskListRow & { id: string }>({
  rows, caption, hide = [], onChanged, isLoading, error, onRetry, emptyState, readOnly = false, withManageActions = false,
}: {
  rows: T[];
  caption: string;
  hide?: string[];
  onChanged: () => void;
  isLoading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  emptyState?: ReactNode;
  /** D-265 (QA-D265 #2) - the archive view: no start button and no row actions. */
  readOnly?: boolean;
  /** DQ-178 - add "רישום שעות" and "מחיקה" (for a task manager) to each row's ⋯. */
  withManageActions?: boolean;
}) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const manage = useTaskDeleteAction({ withLogTime: true, onChanged });
  const shownRows = useWithoutHidden(DELETE_KINDS.ticket, rows); // a task waiting for its delayed DELETE is already out of the list
  const rowKit = useTaskRowActions({ startInColumn: true, onChanged, ...(withManageActions ? { extra: (tk: any) => manage.items(tk) } : {}) });
  const columns: Column<T>[] = [...(readOnly ? [] : [rowKit.startColumn() as unknown as Column<T>]), ...taskListColumns<T>(t, readOnly ? { hide } : { hide, statusCell: (tk) => rowKit.statusCell(tk) })];
  const card = (tk: T): CardSpec => ({
    title: <bdi>{tk.title}</bdi>,
    end: tk.due_date ? <DueWithLate task={tk} t={t} /> : undefined,
    badges: <StatusBadge status={tk.status as any} />,
    metaItems: [assigneeNames(tk as any).join(', ')].filter(Boolean),
  });
  return (
    <>
      {/* QA-F3 (QA-R3 #1, QA-R1 #10) — from md up the table uses DataTable's own
          sideways scroll (mobile="scroll"): no column or ⋯ is clipped, the task
          name keeps a readable width (min-w-40) and stays sticky, and there is no
          sticky header to cover the first row. Phones keep the cards. */}
      <DataTable
        columns={columns}
        data={withManageActions ? shownRows : rows}
        keyField="id"
        caption={caption}
        mobile={isDesktop ? 'scroll' : 'cards'}
        getRowHref={(tk) => taskPath(tk.id)}
        rowLabel={(tk) => tk.title}
        card={card}
        rowActions={readOnly ? undefined : (tk) => rowKit.rowActions(tk as any)}
        isLoading={isLoading}
        skeletonRows={4}
        error={error ? true : undefined}
        onRetry={onRetry}
        emptyState={emptyState}
      />
      {rowKit.dialogs}
      {withManageActions && manage.dialogs}
    </>
  );
}
